/** One-screen best/worst selection (spec 36-02). List order IS the rank. See ADR-0002 §2. */

// Mirrors `MAXDIFF_MODE_MULTI_SET` / `MAXDIFF_MODE_BEST_WORST` in the backend's
// `constants/interview_constants.py` — pinned by `bestWorstModeConstantsMatchBackend`
// (`__tests__/bestWorst.test.ts`) and `test_frontend_mode_constants_match` (backend).
export const MAXDIFF_MODE_MULTI_SET = 'multi_set';
export const MAXDIFF_MODE_BEST_WORST = 'best_worst_single_set';

export interface BestWorstState {
  best: string[];
  worst: string[];
}

export interface Quotas {
  nBest: number;
  nWorst: number;
}

export type BestWorstSide = 'best' | 'worst';

export interface Anchors {
  best: string;
  worst: string;
}

// The shipped literals, so an untouched survey is byte-identical on screen (ADR-0003 §2).
export const ANCHOR_DEFAULTS: Anchors = { best: 'Most Appealing', worst: 'Least Appealing' };

/** Long enough for "Least likely to buy"; short enough for a rail header on a 360px phone. */
export const ANCHOR_MAX_LENGTH = 28;

export function emptyState(): BestWorstState {
  return { best: [], worst: [] };
}

const without = (list: string[], option: string) => list.filter((o) => o !== option);

/** The words the respondent sees, for either MaxDiff mode. Guarded here, not on save — ADR-0003 §2. */
export function resolveAnchors(
  config?: { best_label?: string; worst_label?: string } | null,
): Anchors {
  const one = (raw: string | undefined, fallback: string) =>
    (raw ?? '').trim().slice(0, ANCHOR_MAX_LENGTH) || fallback;
  return {
    best: one(config?.best_label, ANCHOR_DEFAULTS.best),
    worst: one(config?.worst_label, ANCHOR_DEFAULTS.worst),
  };
}

/** Put one option on one side, or take it off it. A full side refuses — ADR-0003 §1. */
export function setSide(
  state: BestWorstState,
  option: string,
  side: BestWorstSide,
  quotas: Quotas,
): BestWorstState {
  const quota = side === 'best' ? quotas.nBest : quotas.nWorst;

  if (state[side].includes(option)) {
    return side === 'best'
      ? { ...state, best: without(state.best, option) }
      : { ...state, worst: without(state.worst, option) };
  }
  if (state[side].length >= quota) return state;

  // Per side, not computed keys: the cast that would silence TS also hides a wrong key.
  return side === 'best'
    ? { best: [...state.best, option], worst: without(state.worst, option) }
    : { best: without(state.best, option), worst: [...state.worst, option] };
}

export function isComplete(state: BestWorstState, quotas: Quotas): boolean {
  return state.best.length === quotas.nBest && state.worst.length === quotas.nWorst;
}

export function toResponseValue(state: BestWorstState) {
  const rank = (list: string[]) => list.map((option, i) => ({ option, rank: i + 1 }));
  return { best: rank(state.best), worst: rank(state.worst) };
}

export type BestWorstStage = 'best' | 'worst' | 'review';

export interface BestWorstAsk {
  phase: BestWorstSide | 'review';
  /** `refill` is ONLY a settled side that reopened — ADR-0003 addendum. */
  kind: 'start' | 'continue' | 'refill';
  remaining: number;
}

const filled = (state: BestWorstState, side: BestWorstSide, quotas: Quotas) =>
  state[side].length >= (side === 'best' ? quotas.nBest : quotas.nWorst);

/** Forward only. A side finished once is never asked for from scratch again — ADR-0003 §5. */
export function advanceStage(
  state: BestWorstState,
  quotas: Quotas,
  stage: BestWorstStage,
): BestWorstStage {
  let next = stage;
  if (next === 'best' && filled(state, 'best', quotas)) next = 'worst';
  if (next === 'worst' && filled(state, 'worst', quotas)) next = 'review';
  return next;
}

/** What is still outstanding, and whether it is a first ask or a top-up — ADR-0003 §5. */
export function nextAsk(
  state: BestWorstState,
  quotas: Quotas,
  stage: BestWorstStage,
): BestWorstAsk {
  if (filled(state, 'best', quotas) && filled(state, 'worst', quotas)) {
    return { phase: 'review', kind: 'start', remaining: 0 };
  }
  if (stage === 'best') {
    return {
      phase: 'best',
      kind: state.best.length === 0 ? 'start' : 'continue',
      remaining: quotas.nBest - state.best.length,
    };
  }
  if (!filled(state, 'worst', quotas)) {
    return {
      phase: 'worst',
      kind: state.worst.length === 0 ? 'start' : 'continue',
      remaining: quotas.nWorst - state.worst.length,
    };
  }
  // Both stages behind us and the top short: a settled pick was dragged across.
  return { phase: 'best', kind: 'refill', remaining: quotas.nBest - state.best.length };
}

/**
 * Best/worst is its own question type — ADR-0003 §6.
 *
 * Mirrors `QUESTION_TYPE_BEST_WORST` in the backend's `constants/interview_constants.py`, pinned by
 * `bestWorstTypeConstantMatchesBackend` here and `test_best_worst_type_constant_matches` there.
 */
export const QUESTION_TYPE_BEST_WORST = 'best_worst';

interface QuestionLike {
  type?: string;
  max_diff_config?: { mode?: string } | null;
}

/**
 * Both spellings, one predicate.
 *
 * Questions authored before the split carry `max_diff` with `mode: best_worst_single_set`. They keep
 * working untouched: a migration rewriting live surveys is a far bigger risk than honouring the old
 * spelling here, and every consumer asks this rather than testing the type itself.
 */
export function isBestWorstQuestion(question?: QuestionLike | null): boolean {
  if (!question) return false;
  if (question.type === QUESTION_TYPE_BEST_WORST) return true;
  return question.type === 'max_diff' && question.max_diff_config?.mode === MAXDIFF_MODE_BEST_WORST;
}
